import theme from "@components/theme";
import { Box, Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'tools/i18n';
import { useContext, useEffect, useState } from 'react';
import { TrackingContext } from '../../../providers/tracking';
import { TrackVisibility } from '@tools/TrackVisibility';
import ProductItem from '@components/products/item';
import { getIsAvailable, getProductPrices, getProductUrl, hasVariants } from '@functions/productOps';
import ProductThumbnail from '@components/products/thumbnail';
import { useRouter } from 'next/router';
import NewportDrawer from '@components/drawer';
import StorefrontApiService from '@services/StorefrontApiService';
import useApplicationStore from '@services/stores/ApplicationStore';

const useStyles = makeStyles(
    {
        box: {
            position: 'absolute',
            top: 7,
            right: 0,
            pointerEvents: 'none',
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            [theme.breakpoints.up('sm')]: {
                right: 14
            },
            '&.open': {
                width: 'calc(100% - 28px)'
            }
        },
        mainButton: {
            paddingRight: 0,
            opacity: 0,
            transition: '0.3s ease',
            cursor: 'context-menu',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
                background: 'transparent',
            },
            '.live > &': {
                opacity: 1,
                pointerEvents: 'auto'
            },
            '& span': {
                display: 'flex',
                flexDirection: 'row-reverse',
                gap: 5,
            },
        },
        icon: {
            background: '#fff',
            width: 37,
            height: 37,
            padding: 10,
            borderRadius: '50%',
            display: 'flex',
            fontSize: 20,
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0px 2px 2px -1px rgba(0,0,0,0.2)'
        },
        fullLabel: {
            height: 'auto',
            transformOrigin: 'left center',
            transition: '0.2s ease-out',
            overflow: 'hidden',
            fontSize: 11,
            padding: '7px 10px',
            lineHeight: '1.1',
            background: '#fff',
            borderRadius: 2,
            boxShadow: '0px 2px 2px -1px rgba(0,0,0,0.2)',
            '&:hover': {
                background: theme.palette.primary.main,
                color: '#fff',
            }
        },
        desktopDialog: {
            position: 'absolute',
            top: 40,
            width: '100%',

            display: 'none',
            left: 0,
            right: 0,
            background: '#fff',
            boxShadow: '0px 2px 2px -1px rgba(0,0,0,0.2)',
            border: '1px solid #eaeaea',
            padding: '2em 3em',
            borderRadius: 3,
            '&.open': {
                display: 'flex'
            }
        },
        productList: {
            width: 'min(550px, calc(100vw - 50px))',
            marginTop: 5,
            opacity: 0,
            background: '#fff',
            boxShadow: '0px 2px 2px -1px rgba(0,0,0,0.2)',
            padding: '20px 15px',
            listStyleType: 'none',
            transition: '0.15s ease-in',
            '&.open': {
                opacity: 1,
            },
        },
        productGrid: {
            pointerEvents: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            gridTemplateColumns: "repeat(2, minmax(0, 50%))",
            gap: 4,
            [theme.breakpoints.up('sm')]: {
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 3fr))",
                gap: '20px 5px'
            },
            [theme.breakpoints.up('md')]: {
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: '20px 5px'
            },
            '& > div': {
                maxWidth: '300px',
            }
        },
        item: {
            color: '#222',
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: '80px 1fr',
            gap: '1rem',
            width: '100%',
            '& a': {
                color: '#222',
                textDecoration: 'none',
            }
        },
        productCard: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            '& button': {
                marginTop: 'auto',
            },
        },
        item__image: {
            display: 'flex',
            position: 'relative',
            marginRight: 15,
            flexShrink: 0,
            width: '100%',
            height: 'fit-content',
            margin: 'auto 0',
            '.compact &': {
                width: 80,
            },
            [theme.breakpoints.down('sm')]: {
                width: 80,
            },
            '&:after': {
                content: '""',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                position: 'absolute',
                mixBlendMode: 'multiply',
                pointerEvents: 'none',
            },
        },
        buttonGroup: {
            display: 'flex',
            gap: 5,
            alignItems: 'flex-end',
            '& > button': {
                right: 'unset',
                left: 'unset',
                bottom: 'unset',
                position: 'relative',
                width: '50%',
                padding: 7,
            }
        },
        actions: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'end',
        },
    },

    { name: 'ShopTheLook' },
);

export default function ShopTheLook({block, slots}) {
    const { t } = useTranslation();
    const { actions } = useContext(TrackingContext);
    const router = useRouter();
    const classes = useStyles();
    const relatedMediaUrl = slots?.[0]?.config?.relatedMedia?.value || ''
    const relatedProductIds = slots?.[0]?.config?.products?.value || [];
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [isLive, setIsLive] = useState(false);
    const [boxOpen, setBoxOpen] = useState(false);
    const api = new StorefrontApiService();
    const isMobile = useApplicationStore((state) => state.isMobile);


    useEffect(async () => {
        const populatedProducts =  await api.getProductByIds(relatedProductIds);
        setRelatedProducts(populatedProducts.products)
        findImage()
    }, [relatedProductIds]);

    function toggleList(event) {
        event.preventDefault();
        setBoxOpen(!boxOpen);
    }

    function findImage() {
        const box = document.getElementById(`shop-the-look-${relatedMediaUrl}`);
        const image = document.querySelector(`img[src*='${relatedMediaUrl}']`);

        if (image) {
            setIsLive(true);
            const closestLink = image.closest('a');
            if (closestLink) {
                closestLink.insertAdjacentElement('afterend', box)
                closestLink.parentElement.style.position = 'relative'
            } else {
               image.parentElement.appendChild(box)
            }
        }
    }

   function renderProducts(sizes) {
        return relatedProducts?.length && relatedProducts.length > 0 && relatedProducts.map((product, key) => {

            return <ProductItem
                id={product.id}
                productNumber={product.productNumber}
                name={product.translated.name}
                link={getProductUrl(product)}
                prices={getProductPrices(product)}
                image={<ProductThumbnail imageType={'standard'} product={product} alt={product.translated?.name} index={key} key={key}/>}
                categoryBreadcrumb={''}
                list={{id: block.sectionId, name: 'cms_newport_product_block', location: router.asPath }}
                manufacturer={product.manufacturer ? product.manufacturer.translated.name : ''}
                available={getIsAvailable(product)}
                variants={hasVariants(product)}
                stock={product.availableStock}
                deliveryTimeString={product.deliveryTime}
                fastDelivery={
                    !!(
                        product?.translated?.customFields?.fast_delivery_available &&
                        config.language.locale === 'sv'
                    )
                }
                addToCart={'minimal'}
                position={key + 1}
                sizes={sizes}
                lazyHydrate={false}
                key={`${product.parentId}-${product.id}-${key}`}
                propertyIds={product.propertyIds || []}
                streamIds={product.streamIds}
                analytics={product?.extensions?.newport?.analytics}
            />
        });
    }


    return (
        <TrackVisibility onVisible={findImage}>
            <div id={`shop-the-look-${relatedMediaUrl}`} className={[classes.box, isLive ? 'live' : '', boxOpen ? 'open' : ''].join(' ')}>
                <Button className={classes.mainButton} onClick={toggleList}>
                    <span className={classes.fullLabel}>{t('shop-the-look-button-effect')}</span>
                </Button>


                {
                    isMobile ?
                        <NewportDrawer open={boxOpen} close={toggleList} showClose={true} anchor={'bottom'} >
                            <Grid className={[classes.productListDrawer, boxOpen ? 'open' : ''].join(' ')} xs={12} item>
                                <h3>{t('shop-the-look-title')}</h3>
                                <p>{t('shop-the-look-subtitle')}</p>
                                <Box
                                    component="div"
                                    className={[classes.productGrid, 'product-grid'].join(' ')}
                                    style={{ display: 'grid' }}
                                    key={'box-key55'}
                                >
                                    {renderProducts({ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 })}
                                </Box>
                            </Grid>
                        </NewportDrawer>
                        :
                        <div className={[classes.desktopDialog, boxOpen ? 'open' : ''].join(' ')}>
                            <Grid className={classes.productListDrawer} xs={12} item>
                                <h3>{t('shop-the-look-title')}</h3>
                                <p>{t('shop-the-look-subtitle')}</p>

                                <Box
                                    component="div"
                                    className={[classes.productGrid, 'product-grid'].join(' ')}
                                    style={{ display: 'grid' }}
                                    key={'box-key55'}
                                >
                                    {renderProducts({ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 })}
                                </Box>
                            </Grid>
                        </div>
                }
            </div>
        </TrackVisibility>
    );
}
